import { useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import {
  formatIsoDate,
  renderStatus,
  formatName,
  isProgramChairPositionName,
} from "../../utils/generalUtils";

import GeneratePdf from "../../generatePdf/GeneratePdf";

import SpectatorTable from "../../components/Table/SpectatorTable";
import useSpectateCosService from "../../services/Spectator/spectateCosService";
import usePositionService from "../../services/PositionAdmin/positionService";
import { Input, Select, Space } from "antd";

const { Search } = Input;

const AllCos = () => {
  const { getCosFromAppId, cosLoading, cosResponse } = useSpectateCosService();
  const { getAllApprovers, approverResponse } = usePositionService();
  const { adminAllCosPopup } = useSelector((state) => state.generalSlice);

  const mappedApprovers = useMemo(() => {
    // Program Chairs only approve FSR — COS is Dean-level (or non-faculty
    // approver) territory, so they're excluded here.
    return (approverResponse?.data || [])
      .filter((item) => !isProgramChairPositionName(item?.position_name))
      .map((item) => ({
        value: item?.id,
        label: `${item?.position_name} - ${item?.unit}`,
      }));
  }, [approverResponse]);

  const [selectedPos, setSelectedPos] = useState();
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    getAllApprovers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedPos && mappedApprovers.length > 0) {
      const first = mappedApprovers[0].value;
      setSelectedPos(first);
      getCosFromAppId(first);
    }
  }, [mappedApprovers, selectedPos, getCosFromAppId]);

  const handleRefresh = (posId) => {
    getAllApprovers();
    getCosFromAppId(posId);
  };

  const handlePosSelect = (posId) => {
    setSelectedPos(posId);
    getCosFromAppId(posId);
  };

  useEffect(() => {
    handleRefresh(selectedPos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allMappedData =
    cosResponse?.data?.map((item, index) => ({
      key: index + 1,
      submitter: `${item?.submission?.submitter?.last_name || ""}, ${
        item?.submission?.submitter?.first_name || ""
      }`.trim(),
      formattedName: formatName(
        item?.submission?.submitter?.first_name,
        item?.submission?.submitter?.middle_name,
        item?.submission?.submitter?.last_name,
        "lfmcut"
      ),
      approver_name: `${item?.approver?.last_name || ""}, ${
        item?.approver?.first_name || ""
      }`.trim(),
      formattedApproverName: formatName(
        item?.approver?.first_name,
        item?.approver?.middle_name,
        item?.approver?.last_name,
        "lfmcut"
      ),
      ...item?.submission?.certificate_of_service,
      submitter_position: item?.submission?.position?.position_name,
      submitter_position_unit: item?.submission?.position?.unit,
      date_submitted: item?.submission?.created_at
        ? formatIsoDate(item?.submission?.created_at)
        : null,
      status: item?.status || "no data",
      submission_id: item?.submission?.id,
      approval_id: item?.id,
      approval_date: item?.approval_date,
    })) || [];

  const mappedData = searchValue
    ? allMappedData.filter((item) =>
        [item.submitter, item.approver_name]
          .filter(Boolean)
          .some((val) => val.toLowerCase().includes(searchValue.toLowerCase()))
      )
    : allMappedData;

  const columns = [
    {
      title: "No.",
      dataIndex: "key", width: 60,
      rowScope: "row",
    },
    {
      title: "Submitter",
      dataIndex: "submitter",
      key: "submitter",
    },
    {
      title: "Approver",
      dataIndex: "approver_name",
      key: "approver_name",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"],
        ["cancelled"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => {
        return (
          <GeneratePdf
            docuType="cos"
            contents={item}
            disabled={item?.status !== "approved"}
          />
        );
      },
    },
  ];

  const viewingDataConfig = [
    {
      key: 1,
      collapseName: "Required Details",
      collapseItems: [
        {
          key: 1,
          name: "start_date",
          label: "Start Date",
        },
        {
          key: 2,
          name: "end_date",
          label: "End Date",
        },
        {
          key: 3,
          name: "absent",
          label: "Date of Absences",
        },
      ],
    },
    {
      key: 1,
      collapseName: "Additional Details",
      collapseItems: [
        {
          key: 1,
          name: "act1",
          label: "Activity 1",
        },
        {
          key: 2,
          name: "week1_hours",
          label: "No. of hours per week 1",
        },
        {
          key: 3,
          name: "act2",
          label: "Activity 2",
          hideIfEmpty: true,
        },
        {
          key: 4,
          name: "week2_hours",
          label: "No. of hours per week 2",
          hideIfEmpty: true,
        },
        {
          key: 5,
          name: "act3",
          label: "Activity 3",
          hideIfEmpty: true,
        },
        {
          key: 6,
          name: "week3_hours",
          label: "No. of hours per week 3",
          hideIfEmpty: true,
        },
        {
          key: 7,
          name: "act4",
          label: "Activity 4",
          hideIfEmpty: true,
        },
        {
          key: 8,
          name: "week4_hours",
          label: "No. of hours per week 4",
          hideIfEmpty: true,
        },
        {
          key: 9,
          name: "act5",
          label: "Activity 5",
          hideIfEmpty: true,
        },
        {
          key: 10,
          name: "week5_hours",
          label: "No. of hours per week 5",
          hideIfEmpty: true,
        },
      ],
    },
  ];

  return (
    <>
      {cosLoading && "loading..."}
      {!cosLoading && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <Space>
              <p>See submissions under: </p>
              <Select
                defaultValue={selectedPos}
                options={mappedApprovers}
                onChange={handlePosSelect}
                popupMatchSelectWidth={false}
              />
            </Space>
            <Search
              placeholder="Search submitter or approver..."
              value={searchValue}
              size="large"
              allowClear
              enterButton
              onSearch={setSearchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ maxWidth: 400 }}
            />
          </div>
          <SpectatorTable
            tableColumn={columns}
            tableData={mappedData}
            popupSlice={adminAllCosPopup}
            popupName="adminAllCosPopup"
            docuType="Certificate of Service"
            viewingDataConfig={viewingDataConfig}
          />
        </>
      )}
    </>
  );
};

export default AllCos;
